import React, { useContext, useState } from "react"
import { motion, useViewportScroll } from "framer-motion"
import logo from "../images/gavazzi-logo.svg"
import { graphql, Link, useStaticQuery } from "gatsby"
import { LanguageContext } from "../locales/langContext"
import DropdownMenu from "./dropdownMenu"
import DropdownMobileNav from "./dropdownMobileNav"
import { useApplication } from "../hooks/useApplication"
import { useMenu } from "../hooks/useMenu"
import { useMenuApplication } from "../hooks/useMenuApplication"
import Search from "../images/gavazzi-search.svg"
import Back from "../images/gavazzi-arrow-back.svg"
import X from "../images/gavazzi-cross.svg"

import NestedDropdownMenu from "./nestedDropdownMenu"
import { Box, Container, Flex, Image, Text } from "@theme-ui/components"
import { Menu } from "react-feather"
import {
  getMenuApplicationLink,
  getCareersPath,
  getContactPath,
  getHomePath,
  getSearchPath,
  getWherePath,
  getBlogPath,
  getNewsPath,
} from "./pathUtils"
import LanguageSwitcher from "./languageSwitcher"

const Nav = () => {
  const data = useStaticQuery(graphql`
    query NavQuery {
      allDatoCmsHelloBar {
        nodes {
          id
          text
          show
          locale
        }
      }
    }
  `)

  const [showHeader, setShowHeader] = useState(true)
  const [show, setShow] = useState(false)
  const [currentMenu, setCurrentMenu] = useState("main") // "main" o id del menu corrente
  const [menuStack, setMenuStack] = useState([]) // Stack per tenere traccia della navigazione
  const { scrollY } = useViewportScroll()
  scrollY.onChange(() => {
    return scrollY.get() > 100 ? setShowHeader(false) : setShowHeader(true)
  })
  const languageContext = useContext(LanguageContext)
  const helloBar = data.allDatoCmsHelloBar.nodes.filter(obj => {
    return obj.locale === languageContext.locale
  })

  const applications = useApplication().filter(obj => {
    return obj.locale === languageContext.locale
  })

  const menuApplications = useMenuApplication(languageContext.locale)

  const menu = useMenu(languageContext.locale)

  const goToSubmenu = menuItem => {
    if (menuItem.treeChildren && menuItem.treeChildren.length > 0) {
      setMenuStack(prev => [
        ...prev,
        { id: currentMenu, title: getCurrentMenuTitle() },
      ])
      setCurrentMenu(menuItem.id)
    }
  }

  const goBack = () => {
    if (menuStack.length > 0) {
      const previousMenu = menuStack[menuStack.length - 1]
      setMenuStack(prev => prev.slice(0, -1))
      setCurrentMenu(previousMenu.id)
    } else {
      setCurrentMenu("main")
    }
  }

  const handleMobileMenuClose = () => {
    setShow(false)
    setCurrentMenu("main")
    setMenuStack([])
  }

  const getCurrentMenuItems = () => {
    if (currentMenu === "main") {
      return []
    }

    // Funzione ricorsiva per trovare un elemento per ID in tutto l'albero
    const findItemById = (items, targetId) => {
      for (const item of items) {
        if (item.id === targetId) {
          return item
        }
        if (item.treeChildren && item.treeChildren.length > 0) {
          const found = findItemById(item.treeChildren, targetId)
          if (found) return found
        }
      }
      return null
    }

    // Cerca in tutti gli alberi possibili
    const allItems = [...menuApplications, ...menu]
    const currentItem = findItemById(allItems, currentMenu)
    return currentItem?.treeChildren || []
  }

  const getCurrentMenuTitle = () => {
    if (currentMenu === "main") {
      return ""
    }

    // Funzione ricorsiva per trovare un elemento per ID
    const findItemById = (items, targetId) => {
      for (const item of items) {
        if (item.id === targetId) {
          return item
        }
        if (item.treeChildren && item.treeChildren.length > 0) {
          const found = findItemById(item.treeChildren, targetId)
          if (found) return found
        }
      }
      return null
    }

    const allItems = [...menuApplications, ...menu]
    const currentItem = findItemById(allItems, currentMenu)
    return currentItem?.title || currentItem?.name || ""
  }

  return (
    <LanguageContext.Consumer>
      {t => (
        <Box
          as={motion.header}
          animate={{
            backgroundColor: showHeader
              ? "rgba(255, 255, 255, 1)"
              : "rgba(255, 255, 255, 0.85)",
          }}
          transition={{ duration: 0.5 }}
          sx={{
            backgroundColor: "white",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 1000,
          }}
        >
          {helloBar[0].show && (
            <Box
              sx={{
                textAlign: "center",
                px: [1, 0],
                py: [3],
                fontSize: [2, 2],
                backgroundColor: "primary",
                color: "white",
                a: {
                  color: "white",
                  textDecoration: "underline",
                },
              }}
              dangerouslySetInnerHTML={{ __html: helloBar[0].text }}
            />
          )}

          <Container
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "row",
              flexWrap: ["nowrap", "nowrap", "wrap"],
              justifyContent: "space-between",
              alignItems: "center",
              maxWidth: "1540px",
              paddingY: [2, 3],
              gap: ["10px", "10px", "24px", "40px"],
            }}
          >
            {/* LOGO */}
            <Box sx={{}}>
              <Link to={getHomePath(t.locale)}>
                <Image
                  sx={{
                    width: ["125px", "125px", "180px", "250px"],
                    minWidth: ["60px", "60px", "140px", "250px"],
                    minHeight: ["40px", "60px", "50px", "60px"],
                    "@media screen and (max-width: 1199px)": {
                      width: "160px",
                      minWidth: "120px",
                      minHeight: "45px",
                    },
                  }}
                  src={logo}
                  alt="Logo"
                />
              </Link>
            </Box>

            {/* HAMBURGER - mobile only, hidden on desktop */}
            <Box sx={{ display: ["block", "block", "none", "none"], ml: "auto" }}>
              <Menu role="button" tabIndex={0} onClick={() => setShow(!show)} />
            </Box>
            <Box sx={{ display: ["none", "none", "flex", "flex"], flexWrap: "wrap", flexGrow: 1, flexDirection: "column-reverse", gap: "10px" }}>

              {/* PRIMARY MENU - desktop only */}
              <Box
                sx={{
                  display: ["none", "none", "flex", "flex"],
                  gap: ["20px", "20px", "30px", "40px"],
                  flexDirection: "row",
                  listStyle: "none",
                  height: "100%",
                  flexBasis: 1,
                  fontSize: ["16px", "16px", "14px", "18px"],
                  "& > a": {
                    textDecoration: "none",
                  },
                }}
              >
                {menuApplications.map(item => {
                  const label = item.application
                    ? item.title || item.application.title
                    : item.title
                  const hasChildren =
                    item.treeChildren && item.treeChildren.length > 0

                  if (hasChildren) {
                    return (
                      <NestedDropdownMenu
                        key={item.id}
                        anchor={label}
                        links={item.treeChildren}
                        path={(label || "").toLowerCase()}
                      />
                    )
                  }

                  const itemLink = getMenuApplicationLink(
                    item.application,
                    t.locale
                  )

                  return (
                    <Box
                      key={item.id}
                      sx={{ paddingTop: "16px", paddingBottom: "16px" }}
                    >
                      {itemLink?.external ? (
                        <a
                          href={itemLink.href}
                          target={itemLink.newWindow ? "_blank" : undefined}
                          rel={
                            itemLink.newWindow
                              ? "noopener noreferrer"
                              : undefined
                          }
                          style={{ textDecoration: "none" }}
                        >
                          {label}
                        </a>
                      ) : (
                        <Link
                          to={itemLink ? itemLink.href : "/"}
                          style={{ textDecoration: "none" }}
                        >
                          {label}
                        </Link>
                      )}
                    </Box>
                  )
                })}

                {menu.map(rootPage => (
                  <DropdownMenu
                    key={rootPage.id}
                    anchor={rootPage.name}
                    links={rootPage.treeChildren}
                    path={
                      rootPage.link
                        ? rootPage.link.__typename === "DatoCmsPageLink"
                          ? rootPage.link.page.slug
                          : rootPage.link.url
                        : rootPage.treeChildren.length > 0 &&
                          rootPage.treeChildren[0].link &&
                          rootPage.treeChildren[0].link.__typename ===
                          "DatoCmsPageLink"
                          ? rootPage.treeChildren[0].link.page.treeParent.slug
                          : rootPage.name.toLowerCase().replace(/\s+/g, "-")
                    }
                  />
                ))}
                <Box sx={{ paddingTop: "16px", paddingBottom: "16px" }}>
                  <Link
                    to={getNewsPath(t.locale)}
                    style={{
                      textDecoration: "none",
                    }}
                    activeStyle={{
                      borderBottom: "2px solid #00793D",
                      fontWeight: "bold",
                    }}
                  >
                    {t.insight}
                  </Link>
                </Box>
              </Box>

              {/* SECONDARY MENU - desktop only, pushed right */}
              <Box
                sx={{
                  display: ["none", "none", "flex", "flex"],
                  alignItems: "center",
                  ml: "auto",
                  fontSize: 1,
                }}
              >
                <Link
                  to={getBlogPath(t.locale)}
                  style={{
                    textDecoration: "none",
                    marginRight: "1rem",
                  }}
                  activeStyle={{
                    borderBottom: "2px solid #00793D",
                    fontWeight: "bold",
                  }}
                >
                  {t.blog}
                </Link>
                <Link
                  to={getWherePath(t.locale)}
                  style={{
                    textDecoration: "none",
                    marginRight: "1rem",
                  }}
                  activeStyle={{
                    borderBottom: "2px solid #00793D",
                    fontWeight: "bold",
                  }}
                >
                  {t.where}
                </Link>
                <Link
                  to={getCareersPath(t.locale)}
                  style={{
                    textDecoration: "none",
                    marginRight: "1rem",
                  }}
                  activeStyle={{
                    borderBottom: "2px solid #00793D",
                    fontWeight: "bold",
                  }}
                >
                  {t.careers}
                </Link>
                <Link
                  to={getContactPath(t.locale)}
                  style={{
                    textDecoration: "none",
                    marginRight: "1rem",
                  }}
                  activeStyle={{
                    borderBottom: "2px solid #00793D",
                    fontWeight: "bold",
                  }}
                >
                  {t.contacts}
                </Link>
                <Link to={getSearchPath(t.locale)}>
                  <Image
                    width="16px"
                    height="16px"
                    src={Search}
                    alt="Search"
                  />
                </Link>
                <LanguageSwitcher />
              </Box>
            </Box>
            {show && (
              <Box
                sx={{
                  position: "fixed",
                  backgroundColor: "primary",
                  width: "100%",
                  top: 0,
                  left: 0,
                  zIndex: 1000,
                  height: "100%",
                  overflowY: "auto",
                  color: "light",
                }}
              >
                {/* Header con freccia back e titolo */}
                <Flex
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    pt: "40px",
                    pb: "60px",
                    px: "20px",
                  }}
                >
                  <Flex sx={{ alignItems: "center", gap: "10px" }}>
                    {currentMenu !== "main" && (
                      <Image src={Back} alt="Back" onClick={goBack} />
                    )}
                    {currentMenu !== "main" && (
                      <Text
                        sx={{
                          fontSize: "18px",
                          fontWeight: 500,
                          color: "white",
                          mb: 0,
                        }}
                      >
                        {getCurrentMenuTitle()}
                      </Text>
                    )}
                  </Flex>
                  <Image src={X} alt="Close" onClick={handleMobileMenuClose} />
                </Flex>

                {/* Menu principale - solo quando siamo al livello main */}
                {currentMenu === "main" && (
                  <Flex
                    as="ul"
                    sx={{
                      listStyle: "none",
                      margin: 0,
                      padding: 0,
                      flexDirection: "column",
                      px: "21px",
                      gap: "21px",
                      li: {
                        fontSize: "34px",
                        fontWeight: 300,
                      },
                      a: { color: "light" },
                    }}
                  >
                    {menuApplications.map(item => {
                      const label = item.application
                        ? item.title || item.application.title
                        : item.title
                      const hasChildren =
                        item.treeChildren && item.treeChildren.length > 0

                      if (hasChildren) {
                        return (
                          <Box as="li" key={item.id}>
                            <Box
                              onClick={() => goToSubmenu(item)}
                              sx={{
                                cursor: "pointer",
                                color: "white",
                                fontSize: "34px",
                                fontWeight: 300,
                              }}
                            >
                              {label}
                            </Box>
                          </Box>
                        )
                      }

                      const itemLink = getMenuApplicationLink(
                        item.application,
                        languageContext.locale
                      )

                      return (
                        <Box as="li" key={item.id}>
                          {itemLink?.external ? (
                            <a
                              href={itemLink.href}
                              target={itemLink.newWindow ? "_blank" : undefined}
                              rel={
                                itemLink.newWindow
                                  ? "noopener noreferrer"
                                  : undefined
                              }
                              style={{
                                textDecoration: "none",
                                color: "white",
                                fontSize: "34px",
                                fontWeight: 300,
                              }}
                            >
                              {label}
                            </a>
                          ) : (
                            <Link
                              to={itemLink ? itemLink.href : "/"}
                              style={{
                                textDecoration: "none",
                                color: "white",
                                fontSize: "34px",
                                fontWeight: 300,
                              }}
                            >
                              {label}
                            </Link>
                          )}
                        </Box>
                      )
                    })}
                    {menu.map(rootPage => (
                      <DropdownMobileNav
                        key={rootPage.id}
                        anchor={rootPage.name}
                        links={rootPage.treeChildren}
                        path={
                          rootPage.link
                            ? rootPage.link.__typename === "DatoCmsPageLink"
                              ? rootPage.link.page.slug
                              : rootPage.link.url
                            : rootPage.treeChildren.length > 0 &&
                              rootPage.treeChildren[0].link &&
                              rootPage.treeChildren[0].link.__typename ===
                              "DatoCmsPageLink"
                              ? rootPage.treeChildren[0].link.page.treeParent.slug
                              : rootPage.name.toLowerCase().replace(/\s+/g, "-")
                        }
                      />
                    ))}
                    <Box as="li">
                      <Link
                        to={getNewsPath(t.locale)}
                        style={{
                          textDecoration: "none",
                        }}
                        activeStyle={{
                          borderBottom: "2px solid #00793D",
                          fontWeight: "bold",
                        }}
                      >
                        {t.insight}
                      </Link>
                    </Box>
                  </Flex>
                )}

                {/* Menu submenu - quando non siamo al livello main */}
                {currentMenu !== "main" && (
                  <Box
                    sx={{
                      px: "21px",
                      py: "20px",
                    }}
                  >
                    <Flex
                      sx={{
                        flexDirection: "column",
                        gap: "0",
                      }}
                    >
                      {getCurrentMenuItems().map((item, idx) => {
                        return (
                          <Box
                            key={item.id}
                            sx={{
                              borderBottom: "1px solid rgba(255,255,255,0.2)",
                              py: "16px",
                              cursor:
                                item.treeChildren &&
                                  item.treeChildren.length > 0
                                  ? "pointer"
                                  : "default",
                              "&:last-child": {
                                borderBottom: "none",
                              },
                            }}
                          >
                            {/* Layout a colonna: numero sopra, titolo sotto */}
                            <Box sx={{ mb: "8px" }}>
                              <Text
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  color: "rgba(255,255,255,0.7)",
                                  display: "block",
                                }}
                              >
                                {String(idx + 1).padStart(2, "0")}
                              </Text>
                            </Box>
                            <Flex
                              sx={{
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              {item.treeChildren &&
                                item.treeChildren.length > 0 ? (
                                // SE HA FIGLI: non è un link, serve solo per navigare
                                <Box
                                  onClick={() => goToSubmenu(item)}
                                  sx={{ cursor: "pointer", flex: 1 }}
                                >
                                  <Text
                                    sx={{
                                      fontSize: "20px",
                                      fontWeight: 400,
                                      color: "white",
                                    }}
                                  >
                                    {item.title || item.name}
                                  </Text>
                                </Box>
                              ) : item.application ? (
                                // SE NON HA FIGLI MA HA APPLICATION: È UN LINK - usa la logica del megamenu
                                (() => {
                                  const itemLink = getMenuApplicationLink(
                                    item.application,
                                    languageContext.locale
                                  )
                                  const labelStyle = {
                                    fontSize: "20px",
                                    fontWeight: 400,
                                    color: "white",
                                    flex: 1,
                                    textDecoration: "none",
                                    display: "block",
                                    cursor: "pointer",
                                    "&:hover": {
                                      color: "rgba(255,255,255,0.8)",
                                    },
                                  }
                                  const itemLabel =
                                    item.title ||
                                    item.application.title ||
                                    item.name
                                  return itemLink?.external ? (
                                    <a
                                      href={itemLink.href}
                                      target={
                                        itemLink.newWindow
                                          ? "_blank"
                                          : undefined
                                      }
                                      rel={
                                        itemLink.newWindow
                                          ? "noopener noreferrer"
                                          : undefined
                                      }
                                      style={{ textDecoration: "none" }}
                                    >
                                      <Text as="span" sx={labelStyle}>
                                        {itemLabel}
                                      </Text>
                                    </a>
                                  ) : (
                                    <Link to={itemLink ? itemLink.href : "/"}>
                                      <Text as="a" sx={labelStyle}>
                                        {itemLabel}
                                      </Text>
                                    </Link>
                                  )
                                })()
                              ) : (
                                // SE NON HA NÉ FIGLI NÉ APPLICATION: solo testo
                                <Text
                                  sx={{
                                    fontSize: "20px",
                                    fontWeight: 400,
                                    color: "white",
                                    flex: 1,
                                  }}
                                >
                                  {item.title || item.name}
                                </Text>
                              )}
                              {item.treeChildren &&
                                item.treeChildren.length > 0 && (
                                  <Text sx={{ color: "rgba(255,255,255,0.5)" }}>
                                    ›
                                  </Text>
                                )}
                            </Flex>
                          </Box>
                        )
                      })}
                    </Flex>
                  </Box>
                )}

                {/* Footer menu - solo quando siamo al livello main */}
                {currentMenu === "main" && (
                  <Flex
                    as="ul"
                    sx={{
                      listStyle: "none",
                      margin: 0,
                      marginTop: 5,
                      padding: 0,
                      paddingTop: 3,
                      flexDirection: "column",
                      fontSize: 1,
                      borderTop: "1px solid",
                      borderColor: "#198a52",
                      alignItems: "center",
                      li: {
                        paddingX: 3,
                        paddingY: 2,
                      },
                      a: { color: "light" },
                    }}
                  >
                    <Box as="li">
                      <Link
                        to={getBlogPath(t.locale)}
                        style={{
                          textDecoration: "none",
                        }}
                        activeStyle={{
                          borderBottom: "2px solid #00793D",
                          fontWeight: "bold",
                        }}
                      >
                        {t.blog}
                      </Link>
                    </Box>
                    <Box as="li">
                      <Link
                        to={getWherePath(t.locale)}
                        style={{
                          textDecoration: "none",
                        }}
                        activeStyle={{
                          borderBottom: "2px solid #00793D",
                          fontWeight: "bold",
                        }}
                      >
                        {t.where}
                      </Link>
                    </Box>
                    <Box as="li">
                      <Link
                        to={getContactPath(t.locale)}
                        style={{
                          textDecoration: "none",
                        }}
                        activeStyle={{
                          borderBottom: "2px solid #00793D",
                          fontWeight: "bold",
                        }}
                      >
                        {t.contacts}
                      </Link>
                    </Box>
                    <Box as="li">
                      <Link to={getSearchPath(t.locale)}>
                        <Image src={Search} alt="Search" />
                      </Link>
                    </Box>
                    <LanguageSwitcher mobile />
                  </Flex>
                )}
              </Box>
            )}
          </Container>
        </Box>
      )}
    </LanguageContext.Consumer>
  )
}

export default Nav
